import React from 'react';
import { Typography, Breadcrumbs } from '@mui/material';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import FadeIn from 'react-fade-in';
import baseStyles from '../Common/styles';

import banner from '../assets_2026/collection_page_top_banner_img.png';
import year2025Img from '../assets_2026/collection_page_2025_btn.png';
import year2026Img from '../assets_2026/collection_page_2026_btn.png';
import year2027Img from '../assets_2026/collection_page_2027_btn.png';
import year2028Img from '../assets_2026/collection_page_2028_btn.png';

const useStyles = makeStyles((theme) => ({
    ...baseStyles(theme),
    breadcrumbs: {
        margin: '20px 0',
    },
    bannerWrap: {
        position: 'relative',
        marginBottom: '7px',
    },
    banner: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },
    bannerOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        padding: '0 5%',
        color: '#fff',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '3.5rem',
        fontWeight: 400,
        textShadow: '0 2px 8px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.3rem',
        },
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '7px',
        marginBottom: '40px',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    yearCard: {
        position: 'relative',
        display: 'block',
        height: '450px',
        overflow: 'hidden',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
            boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
        },
        [theme.breakpoints.down('sm')]: {
            height: '200px',
        },
    },
    yearCardBg: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'transform 0.3s ease',
        '$yearCard:hover &': {
            transform: 'scale(1.08)',
        },
    },
    yearCardDisabled: {
        cursor: 'default',
        filter: 'grayscale(0.6)',
        opacity: 0.6,
        '&:hover': {
            boxShadow: 'none',
        },
        '&:hover $yearCardBg': {
            transform: 'none',
        },
    },
    yearCardOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 16px',
        color: '#fff',
        textShadow: '0 2px 6px rgba(0,0,0,0.55)',
        pointerEvents: 'none',
    },
    yearNumber: {
        fontWeight: 700,
        fontSize: '3.5rem',
        textTransform: 'uppercase',
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.4rem',
        },
    },
    yearSubtitle: {
        fontWeight: 400,
        fontSize: '2.75rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginTop: '2px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.7rem',
        },
    },
    comingSoonLabel: {
        marginTop: '12px',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.65rem',
        },
    },
}));

const YEARS = [
    { year: 2025, image: year2025Img, available: true },
    { year: 2026, image: year2026Img, available: true },
    { year: 2027, image: year2027Img, available: false },
    { year: 2028, image: year2028Img, available: false },
];

export default function SeasonalCollectionDashboard() {
    const classes = useStyles();

    return (
        <FadeIn>
            <div className={classes.root}>
                <Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
                    <Link color="inherit" component={RouterLink} to="/">JML Home</Link>
                    <Typography color="primary">Seasonal Collection</Typography>
                </Breadcrumbs>

                <div className={classes.bannerWrap}>
                    <img src={banner} alt="Seasonal Collection" className={classes.banner} />
                    <Typography component="div" className={classes.bannerOverlay}>Seasonal Collection</Typography>
                </div>

                <div className={classes.grid}>
                    {YEARS.map(({ year, image, available }) => (
                        available ? (
                            <Link
                                key={year}
                                component={RouterLink}
                                to={`/seasonal-collection/${year}`}
                                className={classes.yearCard}
                                underline="none"
                                aria-label={`${year} Seasonal Collection`}
                            >
                                <div className={classes.yearCardBg} style={{ backgroundImage: `url(${image})` }} />
                                <div className={classes.yearCardOverlay}>
                                    <Typography className={classes.yearNumber}>{year}</Typography>
                                    <Typography className={classes.yearSubtitle}>Seasonal Collection</Typography>
                                </div>
                            </Link>
                        ) : (
                            <div
                                key={year}
                                className={`${classes.yearCard} ${classes.yearCardDisabled}`}
                                aria-disabled="true"
                                aria-label={`${year} Seasonal Collection - Coming Soon`}
                            >
                                <div className={classes.yearCardBg} style={{ backgroundImage: `url(${image})` }} />
                                <div className={classes.yearCardOverlay}>
                                    <Typography className={classes.yearNumber}>{year}</Typography>
                                    <Typography className={classes.yearSubtitle}>Seasonal Collection</Typography>
                                    <Typography className={classes.comingSoonLabel}>Coming Soon</Typography>
                                </div>
                            </div>
                        )
                    ))}
                </div>
            </div>
        </FadeIn>
    );
}
